import React, { createContext, useContext, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Home, Brain, BarChart3, FileText, HelpCircle, LogOut, User, Menu, X, 
  Watch, Clock, Sparkles, BookOpen, ListTodo, Leaf, FlaskConical, ClipboardList, UtensilsCrossed
} from 'lucide-react';

// ══════════════════════════════════════════════════════════════════════════════
// BIOLOGICAL INTERFACE DESIGN SYSTEM
// ══════════════════════════════════════════════════════════════════════════════

// Logo URLs
const SYMBOL_LOGO = "https://customer-assets.emergentagent.com/job_neuro-care-hub-2/artifacts/zntnld86_Lifely%20Symbol%20AI.png";

// Navigation items - simplified, calm
const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: Home },
  { path: '/daily', label: 'Daily Alignment', icon: Clock },
  { path: '/mindflo', label: 'MindFlō', icon: Sparkles },
  { path: '/decode', label: 'My DECODE', icon: Brain },
  { path: '/protocol', label: 'My Pathway', icon: ListTodo },
  { path: '/tracking', label: 'Tracking', icon: BarChart3 },
  { path: '/wearables', label: 'Wearables', icon: Watch },
  { path: '/my-intake', label: 'My Intake', icon: ClipboardList },
  { path: '/my-labs', label: 'My Labs', icon: FlaskConical },
  { path: '/my-reports', label: 'My Reports', icon: FileText },
  { path: '/eat', label: 'Kitchen', icon: UtensilsCrossed },
  { path: '/knowledge', label: 'Learn', icon: BookOpen },
  { path: '/support', label: 'Support & Guidance', icon: HelpCircle },
];

// System State Color System (Light, Organic)
const STATE_THEMES = {
  stable: {
    background: '#E8ECE6',
    text: '#4A5D4E',
    accent: '#738C78',
    ambient: 'https://static.prod-images.emergentagent.com/jobs/77583cff-d958-486d-be4c-00f151831592/images/ec413b60d5f1de4b408cb49d3b316401a3d0affee362c64490a9f141baf48c3b.png'
  },
  improving: {
    background: '#E1EDE4',
    text: '#2E6B3B',
    accent: '#4D9A5E',
    ambient: 'https://static.prod-images.emergentagent.com/jobs/77583cff-d958-486d-be4c-00f151831592/images/df6cd47847e84398d64de7b9335f1152a953db4b117198ce4d71d65b3abc81d8.png'
  },
  fluctuating: {
    background: '#F5ECE1',
    text: '#8C6239',
    accent: '#C4925D',
    ambient: 'https://static.prod-images.emergentagent.com/jobs/77583cff-d958-486d-be4c-00f151831592/images/4134b54a36434d317678d57d4d30c76c1111973d2fd59206552544bd7f6d7935.png'
  },
  declining: {
    background: '#F2E6E6',
    text: '#8A4343',
    accent: '#BD6B6B',
    ambient: 'https://static.prod-images.emergentagent.com/jobs/77583cff-d958-486d-be4c-00f151831592/images/a442674b5aa271d67aaad277454339f266209774b286d266875eb46a17f7631d.png'
  }
};

// System State Context
const SystemStateContext = createContext({
  systemState: 'stable',
  setSystemState: () => {}
});

export const useSystemState = () => useContext(SystemStateContext);

export function SystemStateProvider({ children }) {
  const [systemState, setSystemState] = useState('stable');
  return (
    <SystemStateContext.Provider value={{ systemState, setSystemState, theme: STATE_THEMES[systemState] }}>
      {children}
    </SystemStateContext.Provider>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// BIOLOGICAL INTERFACE LAYOUT
// ══════════════════════════════════════════════════════════════════════════════

export default function BiologicalLayout({ children, systemState = 'stable' }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const stateTheme = STATE_THEMES[systemState] || STATE_THEMES.stable;

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div 
      className="min-h-screen transition-colors duration-700 ease-in-out"
      style={{ backgroundColor: '#FBFaf9' }}
    >
      {/* Ambient Background Layer (System State Mode) */}
      <div 
        className="fixed inset-0 z-0 transition-opacity duration-700 ease-in-out pointer-events-none"
        style={{
          backgroundImage: `url(${stateTheme.ambient})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.15
        }}
      />

      {/* Top Navigation - Soft, Floating */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#FBFaf9]/80 backdrop-blur-sm border-b border-[#EBE8E3]">
        <div className="max-w-[95rem] mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/dashboard" className="flex items-center gap-2 flex-shrink-0" data-testid="nav-logo">
              <img src={SYMBOL_LOGO} alt="Lifely" className="h-9 w-auto" />
              <span className="font-['Outfit'] text-base font-light text-[#2C2A29] tracking-tight hidden xl:block">
                ReGenflō
              </span>
            </Link>

            {/* Desktop Navigation - Soft, Unobtrusive */}
            <div className="hidden lg:flex items-center gap-0.5 overflow-x-auto scrollbar-hide">
              {navItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link key={item.path} to={item.path} data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}>
                    <button
                      className={`px-2.5 py-1.5 text-xs font-['Work_Sans'] rounded-full transition-all duration-300 flex items-center gap-1.5 whitespace-nowrap ${
                        isActive 
                          ? 'bg-[#EBE8E3] text-[#2C2A29]' 
                          : 'text-[#5C5955] hover:text-[#2C2A29] hover:bg-[#F4F2EE]'
                      }`}
                    >
                      <item.icon className="w-3.5 h-3.5" strokeWidth={1.25} />
                      {item.label}
                    </button>
                  </Link>
                );
              })}
            </div>

            {/* Right side */}
            <div className="flex items-center gap-3">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button 
                    className="flex items-center gap-2 px-3 py-2 rounded-full text-[#5C5955] hover:text-[#2C2A29] hover:bg-[#F4F2EE] transition-all duration-300"
                    data-testid="user-menu"
                  >
                    <div 
                      className="w-8 h-8 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: stateTheme.background }}
                    >
                      <User className="w-4 h-4" style={{ color: stateTheme.text }} strokeWidth={1.25} />
                    </div>
                    <span className="hidden sm:block text-sm font-['Work_Sans']">{user?.name?.split(' ')[0]}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-[#FBFaf9] border-[#EBE8E3]">
                  <div className="px-4 py-3">
                    <p className="text-sm font-medium text-[#2C2A29] font-['Work_Sans']">{user?.name}</p>
                    <p className="text-xs text-[#8A8681]">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator className="bg-[#EBE8E3]" />
                  <DropdownMenuItem 
                    className="text-[#5C5955] focus:text-[#2C2A29] focus:bg-[#F4F2EE] cursor-pointer" 
                    data-testid="logout-btn" 
                    onClick={handleLogout}
                  >
                    <LogOut className="w-4 h-4 mr-2" strokeWidth={1.25} />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Mobile menu button */}
              <button
                className="lg:hidden p-2 rounded-full text-[#5C5955] hover:text-[#2C2A29] hover:bg-[#F4F2EE] transition-all duration-300"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" strokeWidth={1.25} /> : <Menu className="w-5 h-5" strokeWidth={1.25} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-[#EBE8E3] bg-[#FBFaf9]/95 backdrop-blur-sm">
            <div className="px-6 py-4 space-y-1">
              {navItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link key={item.path} to={item.path} onClick={() => setMobileMenuOpen(false)}>
                    <div className={`flex items-center gap-3 px-4 py-3 rounded-full transition-all duration-300 ${
                      isActive 
                        ? 'bg-[#EBE8E3] text-[#2C2A29]' 
                        : 'text-[#5C5955]'
                    }`}>
                      <item.icon className="w-5 h-5" strokeWidth={1.25} />
                      <span className="font-['Work_Sans']">{item.label}</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="relative z-10 pt-20 min-h-screen">
        {children}
      </main>
    </div>
  );
}
